
import React, { useState, useEffect } from 'react';
import { getFormById } from '@/lib/supabase/forms';
import { Form } from '@/components/dashboard/shops/types';
import OrderFormFields from './OrderFormFields';
import FormFieldComponent from './form-components/FormField';
import FormLoadingState from './form-components/FormLoadingState';
import { getValueAndSetter } from './form-utils/valueSetterUtils';
import { FormProps } from './form-components/types';

interface DynamicOrderFormProps extends FormProps {
  formId: string;
}

const DynamicOrderForm: React.FC<DynamicOrderFormProps> = ({
  formId,
  quantity,
  setQuantity,
  customerName,
  setCustomerName,
  customerPhone,
  setCustomerPhone,
  customerEmail,
  setCustomerEmail,
  customerAddress,
  setCustomerAddress,
  customerRegion,
  setCustomerRegion,
  customerMunicipality,
  setCustomerMunicipality,
  note,
  setNote
}) => {
  const [form, setForm] = useState<Form | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadForm = async () => {
      try {
        console.log("Chargement du formulaire avec ID:", formId);
        const formData = await getFormById(formId);
        if (formData) {
          console.log("Formulaire chargé avec succès:", formData);
          
          // Transform the form fields to ensure they have labels
          if (formData.fields) {
            formData.fields = formData.fields.map(field => ({
              ...field,
              label: field.label || field.name // Use the existing label or fallback to the name
            }));
          }
          
          setForm(formData);
        } else {
          console.error("Formulaire non trouvé pour l'ID:", formId);
          setError("Formulaire introuvable");
        }
      } catch (err) {
        console.error("Erreur lors du chargement du formulaire:", err);
        setError("Erreur lors du chargement du formulaire");
      } finally {
        setLoading(false);
      }
    };

    if (formId) {
      loadForm();
    } else {
      setLoading(false);
    }
  }, [formId]);

  if (loading) {
    return <FormLoadingState />;
  }

  if (error || !form) {
    console.log("Erreur lors du chargement du formulaire ou formulaire non trouvé, utilisation du formulaire par défaut");
    // Fallback to standard form
    return (
      <OrderFormFields
        quantity={quantity}
        setQuantity={setQuantity}
        customerName={customerName}
        setCustomerName={setCustomerName}
        customerPhone={customerPhone}
        setCustomerPhone={setCustomerPhone}
        customerEmail={customerEmail}
        setCustomerEmail={setCustomerEmail}
        customerAddress={customerAddress}
        setCustomerAddress={setCustomerAddress}
        customerRegion={customerRegion}
        setCustomerRegion={setCustomerRegion}
        customerMunicipality={customerMunicipality}
        setCustomerMunicipality={setCustomerMunicipality}
        note={note}
        setNote={setNote}
      />
    );
  }

  console.log("Rendu du formulaire personnalisé avec les champs:", form.fields);
  console.log("État actuel des valeurs:", {
    customerName,
    customerPhone,
    customerRegion,
    customerMunicipality
  });

  const formProps = {
    quantity,
    setQuantity,
    customerName,
    setCustomerName,
    customerPhone,
    setCustomerPhone,
    customerEmail,
    setCustomerEmail,
    customerAddress,
    setCustomerAddress,
    customerRegion,
    setCustomerRegion,
    customerMunicipality,
    setCustomerMunicipality,
    note,
    setNote
  };

  // Render custom form based on configuration
  return (
    <div className="space-y-4">
      {/* Dynamic fields rendering based on form configuration */}
      {form.fields
        .filter(field => field.enabled) // Only show enabled fields
        .map((field) => {
          const valueSetterPair = getValueAndSetter(field.name, formProps);
          
          if (!valueSetterPair) {
            console.error(`No value/setter found for field: ${field.name}`);
          }
          
          return (
            <FormFieldComponent
              key={field.id}
              field={field}
              valueSetterPair={valueSetterPair}
            />
          );
        })}
    </div>
  );
};

export default DynamicOrderForm;
